import { useId, useMemo } from 'react';

import { ChartCrosshair } from '../ChartCrosshair';
import { ChartLabels } from '../ChartLabels';
import { ChartPriceLines, hasInteractivePriceLines } from '../ChartPriceLines';
import { ChartTradeMarkers } from '../ChartTradeMarkers';
import {
  DEFAULT_CHART_LABEL_PADDING,
  DEFAULT_CHART_PADDING,
  LABEL_PADDING_VERTICAL,
} from '../constants';
import { useChartLabelWidth } from '../hooks/useChartLabelWidth';
import type { ChartPriceLine, ChartProps, ChartTradeMarker, ChartTradeMarkerHover } from '../types';
import { useTimeSeriesChartHover } from './hooks/useTimeSeriesChartHover';
import { TimeSeriesAreaPaths } from './TimeSeriesAreaPaths';
import type {
  TimeSeriesAreaChartHover,
  TimeSeriesAreaCurve,
  TimeSeriesAreaData,
  TimeSeriesChartLabels,
} from './types';
import {
  createBarGeometry,
  createAreaGeometry,
  createTradeMarkerGeometry,
  createTimeSeriesChartContext,
  createTimeSeriesChartTicks,
} from './utils/geometry';

// Opacity of the line and area after the hovered point
const TRAILING_SERIES_OPACITY = 0.25;
const NO_MARKERS: ChartTradeMarker[] = [];

export type TimeSeriesAreaChartProps = ChartProps & {
  data: TimeSeriesAreaData[];
  lineColor: string;
  gradientColor?: string;
  valueDomain?: [number, number];
  valueFloor?: number;
  lineWidth?: number;
  curve?: TimeSeriesAreaCurve;
  labels?: TimeSeriesChartLabels;
  showBars?: boolean;
  barColor?: string;
  markers?: ChartTradeMarker[];
  markerColors?: { buy: string; sell: string };
  priceLines?: ChartPriceLine[];
  onPointHover?: (hover?: TimeSeriesAreaChartHover) => void;
  onMarkerHover?: (hover?: ChartTradeMarkerHover) => void;
};

export function TimeSeriesAreaChart({
  data,
  width,
  height,
  x,
  y,
  lineColor,
  ariaLabel,
  gradientColor = lineColor,
  valueDomain,
  valueFloor,
  lineWidth = 2,
  curve = 'monotoneX',
  padding,
  labels,
  showBars = false,
  barColor = lineColor,
  markers = NO_MARKERS,
  markerColors,
  priceLines,
  onPointHover,
  onMarkerHover,
}: TimeSeriesAreaChartProps) {
  // Strip colons from React IDs because SVG-to-image tools may not
  // resolve unescaped colons in `url(#...)` references.
  const id = useId().replaceAll(':', '');
  const gradientId = `time-series-area-chart-gradient-${id}`;

  const { labelGroupRef: yLabelsRef, labelWidth: yLabelsWidth } = useChartLabelWidth({
    initialWidth: (DEFAULT_CHART_LABEL_PADDING.right ?? 0) - LABEL_PADDING_VERTICAL * 2,
    disabled: padding?.right != null,
  });

  const { top, right, bottom, left } = {
    ...DEFAULT_CHART_PADDING,
    ...(labels
      ? { ...DEFAULT_CHART_LABEL_PADDING, right: yLabelsWidth + LABEL_PADDING_VERTICAL * 2 }
      : undefined),
    ...padding,
  };

  const geometry = useMemo(() => {
    const context = createTimeSeriesChartContext({
      data,
      width,
      height,
      padding: { top, right, bottom, left },
      getMinValue: ({ value }) => value,
      valueDomain,
      valueFloor,
      showBars,
    });

    if (!context) return undefined;

    return {
      context,
      ...createAreaGeometry(context, curve),
      barGeometry: showBars ? createBarGeometry(context) : [],
      markerGeometry: createTradeMarkerGeometry(context, markers, ({ value }) => value),
      ...createTimeSeriesChartTicks(context, labels),
    };
  }, [
    data,
    width,
    height,
    top,
    right,
    bottom,
    left,
    curve,
    valueDomain,
    valueFloor,
    labels,
    showBars,
    markers,
  ]);

  const context = geometry?.context;
  const { activeHover, activeMarkerKey, handlePointerMove, handlePointerLeave, handlePointerEnd } =
    useTimeSeriesChartHover({
      context,
      width,
      height,
      getHoverValue: ({ value }) => value,
      onPointHover,
      markerGeometry: geometry?.markerGeometry,
      onMarkerHover,
    });

  const hoverGeometry = useMemo(() => {
    const { context } = geometry ?? {};
    const hoveredIndex = activeHover?.point ? context?.data.indexOf(activeHover?.point) : undefined;

    if (!context || hoveredIndex == null || hoveredIndex === -1) return undefined;

    const leadingData = context.data.slice(0, hoveredIndex + 1);
    const trailingData = context.data.slice(hoveredIndex);

    return {
      leading: createAreaGeometry({ ...context, data: leadingData }, curve),
      trailing: createAreaGeometry({ ...context, data: trailingData }, curve),
    };
  }, [curve, geometry, activeHover?.point]);

  if (!geometry || !context) {
    return null;
  }

  const { areaPath, linePath, barGeometry, markerGeometry, xTicks, yTicks } = geometry;
  const { plot, yDomain } = context;

  return (
    <svg
      x={x}
      y={y}
      width={width}
      height={height}
      role={hasInteractivePriceLines(priceLines) ? 'group' : 'img'}
      aria-label={ariaLabel}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={gradientColor} stopOpacity={1} />
          <stop offset="100%" stopColor={gradientColor} stopOpacity={0} />
        </linearGradient>
      </defs>
      {barGeometry.map(
        ({ timestamp, x: barX, y: barY, width: barWidth, height: barHeight }, index) => (
          <rect
            key={`${timestamp}-${index}`}
            x={barX}
            y={barY}
            width={barWidth}
            height={barHeight}
            fill={barColor}
          />
        ),
      )}
      <TimeSeriesAreaPaths
        areaPath={hoverGeometry?.leading.areaPath ?? areaPath}
        linePath={hoverGeometry?.leading.linePath ?? linePath}
        gradientId={gradientId}
        lineColor={lineColor}
        lineWidth={lineWidth}
      />
      {hoverGeometry && (
        <TimeSeriesAreaPaths
          {...hoverGeometry.trailing}
          gradientId={gradientId}
          lineColor={lineColor}
          lineWidth={lineWidth}
          opacity={TRAILING_SERIES_OPACITY}
        />
      )}
      {labels && (
        <ChartLabels
          width={width}
          height={height}
          right={right}
          color={labels.color}
          xTicks={xTicks}
          yTicks={yTicks}
          yLabelsRef={yLabelsRef}
        />
      )}
      {markerColors && markers.length > 0 && (
        <ChartTradeMarkers
          geometry={markerGeometry}
          colors={markerColors}
          hoveredMarkerKey={activeMarkerKey}
        />
      )}
      {(onPointHover || onMarkerHover) && (
        <rect
          data-testid="time-series-chart-hit-area"
          x={plot.left}
          y={plot.top}
          width={plot.width}
          height={plot.height}
          fill="transparent"
          className="cursor-crosshair touch-pan-y"
          onPointerDown={handlePointerMove}
          onPointerMove={handlePointerMove}
          onPointerLeave={handlePointerLeave}
          onPointerUp={handlePointerEnd}
          onPointerCancel={handlePointerEnd}
        />
      )}
      {activeHover && !activeMarkerKey && (
        <ChartCrosshair
          activeHover={activeHover}
          plot={plot}
          horizontalLinePosition="point"
          showActiveCircle={true}
          activeCircleColor={lineColor}
        />
      )}
      {priceLines && priceLines.length > 0 && (
        <ChartPriceLines
          lines={priceLines}
          plot={plot}
          yDomain={yDomain}
          yScale={context.yScale}
          height={height}
          formatValue={labels?.formatValue}
        />
      )}
    </svg>
  );
}
